import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPackageById } from '../../store/slices/packageSlice';
import packageService from '../../services/api/packageService';

const PackageFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentPackage, loading } = useSelector((state) => state.packages);
  const isEdit = !!id;
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      name: '',
      nameAr: '',
      description: '',
      duration: 7,
      nightsInMakkah: 4,
      nightsInMadina: 3,
      basePrice: 0,
      currency: 'USD',
      status: 'Draft',
      isFeatured: false,
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      dispatch(fetchPackageById(id));
    }
  }, [id, isEdit, dispatch]);

  useEffect(() => {
    if (currentPackage && isEdit) {
      reset({
        name: currentPackage.name || '',
        nameAr: currentPackage.nameAr || '',
        description: currentPackage.description || '',
        duration: currentPackage.duration || 7,
        nightsInMakkah: currentPackage.nightsInMakkah || 4,
        nightsInMadina: currentPackage.nightsInMadina || 3,
        basePrice: currentPackage.basePrice || 0,
        currency: currentPackage.currency || 'USD',
        status: currentPackage.status || 'Draft',
        isFeatured: currentPackage.isFeatured || false,
      });
    }
  }, [currentPackage, isEdit, reset]);

  const onSubmit = async (data) => {
    try {
      setSubmitLoading(true);
      setError(null);
      
      // Convert numeric strings to numbers
      const packageData = {
        ...data,
        duration: parseInt(data.duration, 10),
        nightsInMakkah: parseInt(data.nightsInMakkah, 10),
        nightsInMadina: parseInt(data.nightsInMadina, 10),
        basePrice: parseFloat(data.basePrice),
        displayOrder: data.displayOrder ? parseInt(data.displayOrder, 10) : null,
        // Auto-generate slug if not provided
        slug: data.slug || data.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      };
      
      if (isEdit) {
        await packageService.updatePackage(id, packageData);
      } else {
        await packageService.createPackage(packageData);
      }
      
      navigate('/packages');
    } catch (err) {
      console.error('Error saving package:', err);
      setError(err.response?.data?.message || 'Error saving package');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEdit ? 'Edit Package' : 'Create New Package'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Package name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Package Name (English)"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="nameAr"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Package Name (Arabic)"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="duration"
                control={control}
                rules={{ required: 'Duration is required', min: { value: 1, message: 'Must be at least 1 day' } }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Duration (Days)"
                    error={!!errors.duration}
                    helperText={errors.duration?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="nightsInMakkah"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Nights in Makkah"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="nightsInMadina"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Nights in Madina"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="basePrice"
                control={control}
                rules={{ required: 'Base price is required', min: { value: 0, message: 'Must be 0 or greater' } }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Base Price"
                    error={!!errors.basePrice}
                    helperText={errors.basePrice?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select {...field} label="Currency">
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                      <MenuItem value="GBP">GBP</MenuItem>
                      <MenuItem value="SAR">SAR</MenuItem>
                      <MenuItem value="PKR">PKR</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      <MenuItem value="Draft">Draft</MenuItem>
                      <MenuItem value="Published">Published</MenuItem>
                      <MenuItem value="Inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="isFeatured"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Featured</InputLabel>
                    <Select
                      {...field}
                      value={field.value ? 'true' : 'false'}
                      onChange={(e) => field.onChange(e.target.value === 'true')}
                      label="Featured"
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitLoading}
                >
                  {submitLoading ? <CircularProgress size={24} /> : isEdit ? 'Update Package' : 'Create Package'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/packages')}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default PackageFormPage;

